import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RequestError } from '../../../interfaces/error.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { Notification } from '../../../interfaces/notification.interface';
import { GetNotificationsResponse } from '../interfaces/get-notifications';

// Debe calzar con NOTIFICATIONS_PAGE_SIZE de la API.
export const NOTIFICATIONS_PAGE_SIZE = 25;

// Las notificaciones vienen de 4 tablas distintas y sus ids se repiten entre tablas
// (un comentario y una clase pueden tener ambos id 120). Usar solo el id pisaba una
// con otra y duplicaba keys de React; la clave real es tipo + id.
export const notificationKey = (notification: Pick<Notification, 'id' | 'type'>) =>
  `${notification.type}-${notification.id}`;

export interface NotificationState {
  notifications: {
    ids: string[];
    entities: Record<string, Notification>;
  };
  unreadNotifications: Notification[];
  page: number;
  noMore: boolean;
  loading: boolean;
  unreadLoading: boolean;
  unreadLoaded: boolean;
  errorCode: number | null;
}

const initialState: NotificationState = {
  notifications: {
    ids: [],
    entities: {},
  },
  unreadNotifications: [],
  page: 1,
  noMore: false,
  loading: false,
  unreadLoading: false,
  unreadLoaded: false,
  errorCode: null,
};

const notificationAdapter = createEntityAdapter<Notification>(notificationKey);

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    getNotificationInitApiAction: (state) => {
      state.errorCode = null;
      state.loading = true;
    },
    getNotificationSuccessApiAction: (state, action: PayloadAction<GetNotificationsResponse>) => {
      notificationAdapter.updateMany(action.payload.notifications, state.notifications);
      state.noMore = action.payload.notifications.length < NOTIFICATIONS_PAGE_SIZE;
      state.loading = false;
    },
    getNotificationFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode ?? 0;
      state.loading = false;
    },
    getUnreadNotificationInitApiAction: (state) => {
      state.unreadLoading = true;
    },
    getUnreadNotificationSuccessApiAction: (state, action: PayloadAction<GetNotificationsResponse>) => {
      state.unreadNotifications = action.payload.notifications;
      state.unreadLoading = false;
      state.unreadLoaded = true;
    },
    getUnreadNotificationFailureApiAction: (state, _action: PayloadAction<RequestError>) => {
      // Se conserva la última lista buena en vez de dejar el menú vacío.
      state.unreadLoading = false;
    },
    postReadInitApiAction: () => {},
    postReadSuccessApiAction: (state, action: PayloadAction<{ id: string; type: string }>) => {
      const key = notificationKey(action.payload);
      const entity = state.notifications.entities[key];
      if (entity) {
        notificationAdapter.updateOne({ ...entity, isRead: true }, state.notifications);
      }
      state.unreadNotifications = state.unreadNotifications.filter(
        (notification) => notificationKey(notification) !== key,
      );
    },
    postReadFailuireApiAction: () => {},
    postReadAllInitApiAction: () => {},
    postReadAllSuccessApiAction: (state) => {
      state.unreadNotifications = [];
      state.notifications.ids.forEach((id) => {
        state.notifications.entities[id].isRead = true;
      });
    },
    postReadAllFailuireApiAction: () => {},
    // Al entrar a la página de notificaciones: limpia SOLO la lista paginada.
    // (Antes también vaciaba el menú de la campana.)
    notificationListResetAction: (state) => {
      notificationAdapter.removeAll(state.notifications);
      state.errorCode = null;
      state.page = 1;
      state.noMore = false;
      state.loading = false;
    },
    notificationClearAction: (state) => {
      notificationAdapter.removeAll(state.notifications);
      state.unreadNotifications = [];
      state.errorCode = null;
      state.page = 1;
      state.noMore = false;
      state.loading = false;
      state.unreadLoading = false;
      state.unreadLoaded = false;
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
  },
});

export const {
  getNotificationInitApiAction,
  getNotificationSuccessApiAction,
  getNotificationFailureApiAction,
  getUnreadNotificationInitApiAction,
  getUnreadNotificationSuccessApiAction,
  getUnreadNotificationFailureApiAction,
  postReadAllInitApiAction,
  postReadAllSuccessApiAction,
  postReadAllFailuireApiAction,
  postReadInitApiAction,
  postReadSuccessApiAction,
  postReadFailuireApiAction,
  notificationListResetAction,
  notificationClearAction,
  increasePageAction,
  resetPageAction,
} = notificationSlice.actions;

export default notificationSlice.reducer;
